import { Fragment, useRef, useState, useEffect, useMemo } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/outline'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { Select, Spin } from 'antd'
import { debounce } from 'lodash'
import { Button, SelectInput } from '../../../components'
import { fetchStores } from '../../../services/storeService'
import { fetchWarehousesOpen } from '../../../services/warehouseSevices'
import { SCOPE_TYPES } from './utils'

const T_PREFIX = 'sales_channels'

const linksFromChannel = channel => {
  if (!channel) return []
  const raw = channel.scope_type === SCOPE_TYPES.WAREHOUSES ? channel.warehouses : channel.stores
  return (raw || []).map(l => ({ id: l.id, name: l.name }))
}

export const SalesChannelFormModal = ({
  open,
  setOpen,
  selectedChannel = null,
  onSubmit,
  isLoading = false,
  serverFieldError = null,
  onClearFieldError = () => {},
}) => {
  const { i18n } = useTranslation()
  const panelRef = useRef(null)
  const nameInputRef = useRef(null)
  const latestSearch = useRef(0)
  const isEditing = !!selectedChannel

  const [name, setName] = useState('')
  const [scopeType, setScopeType] = useState(null)
  const [links, setLinks] = useState([])
  const [storeOptions, setStoreOptions] = useState([])
  const [isSearchingStores, setIsSearchingStores] = useState(false)

  useEffect(() => {
    if (!open) return
    setName(selectedChannel?.name || '')
    setScopeType(selectedChannel?.scope_type || null)
    setLinks(linksFromChannel(selectedChannel))
    setStoreOptions([])
  }, [open, selectedChannel])

  const { data: warehousesData, isFetching: warehousesFetching } = useQuery(['warehouses_open'], fetchWarehousesOpen, {
    enabled: open && scopeType === SCOPE_TYPES.WAREHOUSES,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
  const warehouseOptions = useMemo(() => {
    const raw = warehousesData?.warehouses || warehousesData || []
    return (Array.isArray(raw) ? raw : []).map(w => ({ value: w.id, label: w.name }))
  }, [warehousesData])

  const searchStores = useMemo(
    () =>
      debounce(async query => {
        if (!query || query.length < 2) {
          setStoreOptions([])
          return
        }
        const seq = ++latestSearch.current
        setIsSearchingStores(true)
        try {
          const res = await fetchStores({ name: query, per_page: 20 })
          if (seq !== latestSearch.current) return
          setStoreOptions((res.stores || []).map(s => ({ value: s.id, label: s.name })))
        } catch {
          if (seq === latestSearch.current) setStoreOptions([])
        }
        if (seq === latestSearch.current) setIsSearchingStores(false)
      }, 300),
    [],
  )
  useEffect(() => () => searchStores.cancel(), [searchStores])

  const dimensionOptions = useMemo(
    () => [
      { id: SCOPE_TYPES.STORES, name: i18n.t(`${T_PREFIX}.scope_types.stores`) },
      { id: SCOPE_TYPES.WAREHOUSES, name: i18n.t(`${T_PREFIX}.scope_types.warehouses`) },
    ],
    [i18n],
  )

  const isWarehouseScope = scopeType === SCOPE_TYPES.WAREHOUSES
  const selectValue = useMemo(() => links.map(l => ({ value: l.id, label: l.name })), [links])
  const selectOptions = isWarehouseScope ? warehouseOptions : storeOptions
  const linksLoading = isWarehouseScope ? warehousesFetching : isSearchingStores

  const handleScopeChange = option => {
    setScopeType(option.id)
    setLinks([])
    setStoreOptions([])
  }

  const handleLinksChange = selected => {
    // Channel can never be empty: veto a removal that would clear all links.
    const next = selected.length === 0 ? links : selected.map(s => ({ id: s.value, name: s.label }))
    setLinks(next)
  }

  const handleNameChange = e => {
    setName(e.target.value)
    if (serverFieldError?.field === 'name') onClearFieldError()
  }

  const tagRender = ({ label, onClose }) => {
    const closable = links.length > 1
    return (
      <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-800 rounded px-2 py-0.5 mr-1 my-0.5 text-sm">
        {label}
        {closable && (
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XIcon className="h-3 w-3" />
          </button>
        )}
      </span>
    )
  }

  const isFormValid = useMemo(() => name.trim().length > 0 && !!scopeType && links.length > 0, [name, scopeType, links])

  const handleSubmit = e => {
    e.preventDefault()
    if (!isFormValid) return
    // Backend diffs against stored values (not by param presence), so always sending
    // both name and the full ids array does not create spurious history entries.
    const idsKey = isWarehouseScope ? 'warehouse_ids' : 'store_ids'
    const ids = links.map(l => l.id)
    const payload = isEditing ? { name: name.trim(), [idsKey]: ids } : { name: name.trim(), scope_type: scopeType, [idsKey]: ids }
    onSubmit(payload)
  }

  const handleClose = () => {
    if (isLoading) return
    setOpen(false)
  }

  const nameError = serverFieldError?.field === 'name' ? i18n.t(`${T_PREFIX}.errors.name_taken`) : null

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" initialFocus={nameInputRef} onClose={handleClose}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              ref={panelRef}
              className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
            >
              <div className="flex justify-between items-center mb-4">
                <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                  {isEditing ? i18n.t(`${T_PREFIX}.form.edit_title`) : i18n.t(`${T_PREFIX}.form.create_title`)}
                </Dialog.Title>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-500"
                  onClick={handleClose}
                  aria-label={i18n.t(`${T_PREFIX}.form.cancel`)}
                >
                  <XIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{i18n.t(`${T_PREFIX}.form.name`)}</label>
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={name}
                    onChange={handleNameChange}
                    className={`shadow-sm block w-full sm:text-sm rounded-md ${nameError ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'}`}
                  />
                  {nameError && <p className="mt-1 text-xs text-red-500">{nameError}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{i18n.t(`${T_PREFIX}.form.scope_type`)}</label>
                  {isEditing ? (
                    <div className="shadow-sm block w-full sm:text-sm rounded-md border border-gray-200 bg-gray-50 text-gray-500 px-3 py-2">
                      {i18n.t(`${T_PREFIX}.scope_types.${scopeType}`)}
                    </div>
                  ) : (
                    <SelectInput
                      options={dimensionOptions}
                      value={scopeType ? dimensionOptions.find(o => o.id === scopeType) : null}
                      onChange={handleScopeChange}
                      className="w-full"
                      required
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {isWarehouseScope ? i18n.t(`${T_PREFIX}.form.warehouses`) : i18n.t(`${T_PREFIX}.form.stores`)}
                  </label>
                  <Select
                    mode="multiple"
                    className="w-full"
                    disabled={!scopeType}
                    showSearch
                    optionFilterProp="label"
                    filterOption={isWarehouseScope}
                    onSearch={scopeType === SCOPE_TYPES.STORES ? searchStores : undefined}
                    labelInValue
                    value={selectValue}
                    options={selectOptions}
                    onChange={handleLinksChange}
                    tagRender={tagRender}
                    loading={linksLoading}
                    notFoundContent={linksLoading ? <Spin size="small" /> : undefined}
                    getPopupContainer={() => panelRef.current || document.body}
                    placeholder={i18n.t(`${T_PREFIX}.form.links_placeholder`)}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="secondary" onClick={handleClose} disabled={isLoading} className="px-4">
                    {i18n.t(`${T_PREFIX}.form.cancel`)}
                  </Button>
                  <Button type="primary" buttonType="submit" loading={isLoading} disabled={!isFormValid} className="px-4">
                    {isEditing ? i18n.t(`${T_PREFIX}.form.save`) : i18n.t(`${T_PREFIX}.form.create`)}
                  </Button>
                </div>
              </form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
